import React, { useEffect, useMemo, useRef, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Home as HomeIcon,
  Users,
  TrendingUp,
  LayoutGrid,
  Bell,
  Shield,
  Sparkles,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";

import { BrandLogo } from "@/components/BrandLogo";
import InstallPromptModal from "@/components/InstallPromptModal";
import { BetaCountdownBanner } from "@/components/BetaCountdownBanner";
import { apiGet } from "@/lib/api";
import { qk } from "@/lib/queries";
import { useAuth } from "@/lib/auth";
import { useSubscription } from "@/context/SubscriptionContext";

const TABS = [
  { key: "home", label: "Home", to: "/", icon: HomeIcon },
  { key: "leads", label: "Leads", to: "/leads", icon: Users },
  { key: "grow", label: "Grow", to: "/grow", icon: TrendingUp },
  { key: "more", label: "My Business", to: "/more", icon: LayoutGrid },
];

function useBadgeCount() {
  const { user } = useAuth();
  const { data } = useQuery({
    queryKey: qk.cardBadge,
    queryFn: () => apiGet("/card/notifications/badge"),
    enabled: Boolean(user),
    staleTime: 30_000,
  });
  return (data?.new_card_leads || 0) + (data?.pending_reviews || 0);
}

function isTabActive(pathname, tabTo) {
  if (tabTo === "/") return pathname === "/" || pathname === "";
  return pathname === tabTo || pathname.startsWith(`${tabTo}/`);
}

export function AppShell() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const badge = useBadgeCount();
  const { isAdmin, isActive, daysRemaining, plan } = useSubscription();

  // FAT #19 Phase 3c — Persistent Upgrade link visibility:
  //   • Free plan (no trial window, needs to upgrade to write)
  //   • Beta approaching end (daysRemaining <= 30)
  //   • Any state where user cannot write (isActive=false)
  const showUpgradeLink =
    plan === "Free" ||
    !isActive ||
    (typeof daysRemaining === "number" && daysRemaining <= 30);

  const activeTab = useMemo(() => {
    return (
      TABS.find((t) => isTabActive(pathname, t.to))?.key ||
      "home"
    );
  }, [pathname]);

  // Close mobile menu on route change (used by the desktop sidebar collapse).
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  useEffect(() => setSidebarExpanded(false), [pathname]);

  // FAT #19 Phase 4 — Upgrade nav pulse nudge.
  // <CapabilityGate> writes a `wc.upgradeNudge.pending` flag in
  // sessionStorage when a user clicks a locked pill. The pill click
  // immediately navigates to /upgrade (which is outside the AppShell),
  // so we replay the pulse the next time the AppShell mounts — that's
  // when the user actually sees the nav link again. Once played, we
  // set `wc.upgradeNudge.shown` and clear pending — one pulse per
  // browser session, per spec.
  const [nudgeActive, setNudgeActive] = useState(false);
  useEffect(() => {
    const play = () => {
      setNudgeActive(true);
      window.setTimeout(() => setNudgeActive(false), 6000);
      try {
        sessionStorage.setItem("wc.upgradeNudge.shown", "1");
        sessionStorage.removeItem("wc.upgradeNudge.pending");
      } catch { /* noop */ }
    };
    // Replay on mount if a pending nudge is queued (came back from /upgrade).
    try {
      if (
        sessionStorage.getItem("wc.upgradeNudge.pending") === "1" &&
        !sessionStorage.getItem("wc.upgradeNudge.shown")
      ) {
        play();
      }
    } catch { /* noop */ }
    // Also fire live if a locked pill is clicked without leaving the shell.
    const handler = () => {
      try {
        if (sessionStorage.getItem("wc.upgradeNudge.shown")) return;
      } catch { /* noop */ }
      play();
    };
    window.addEventListener("wc:upgradeNudge", handler);
    return () => window.removeEventListener("wc:upgradeNudge", handler);
  }, []);

  return (
    <div
      className="flex min-h-[100dvh] w-full bg-background text-foreground"
      data-testid="app-shell"
    >
      {/* ------ Desktop sidebar (≥md) ------ */}
      <aside
        className="hidden md:flex md:w-[76px] lg:w-[220px] md:flex-col md:border-r md:border-border/60 md:bg-card md:py-6"
        aria-label="Main navigation"
        data-testid="app-sidebar"
      >
        <div className="mb-8 flex items-center px-4 lg:px-5">
          <div className="hidden lg:block">
            <BrandLogo size="md" />
          </div>
          <div className="lg:hidden">
            {/* Compact mark on collapsed sidebar */}
            <BrandLogo size="sm" className="[&_span:last-child]:hidden" />
          </div>
        </div>
        <nav className="flex flex-1 flex-col gap-1 px-2 lg:px-3">
          {TABS.map((tab) => (
            <SidebarLink key={tab.key} tab={tab} active={activeTab === tab.key} />
          ))}
          {showUpgradeLink ? (
            <NavLink
              to="/upgrade"
              data-testid="nav-upgrade-link"
              data-nudge-active={nudgeActive ? "true" : undefined}
              className={({ isActive: iA }) =>
                [
                  "mt-1 flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition-colors",
                  iA
                    ? "bg-[#FFC630]/20 text-[#7a5a00]"
                    : "border border-[#FFC630]/50 bg-[#FFC630]/10 text-[#7a5a00] hover:bg-[#FFC630]/20",
                  nudgeActive ? "pulse-nudge" : "",
                ].join(" ")
              }
            >
              <Sparkles className="h-5 w-5" aria-hidden />
              <span className="hidden lg:inline">Upgrade</span>
            </NavLink>
          ) : null}
          {isAdmin ? (
            <NavLink
              to="/admin"
              data-testid="nav-admin-link"
              className={({ isActive: iA }) =>
                [
                  "mt-1 flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors",
                  iA
                    ? "bg-primary/12 text-primary"
                    : "text-muted-foreground hover:bg-secondary hover:text-foreground",
                ].join(" ")
              }
            >
              <Shield className="h-5 w-5" aria-hidden />
              <span className="hidden lg:inline">Admin</span>
            </NavLink>
          ) : null}
        </nav>
        <div className="mt-4 px-2 lg:px-3">
          <button
            type="button"
            onClick={() => navigate("/more")}
            className="group flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
            data-testid="sidebar-notifications"
            aria-label="Notifications"
          >
            <span className="relative">
              <Bell className="h-5 w-5" aria-hidden />
              {badge > 0 ? (
                <span
                  className="absolute -right-1 -top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold text-primary-foreground"
                  data-testid="notification-badge"
                >
                  {badge > 9 ? "9+" : badge}
                </span>
              ) : null}
            </span>
            <span className="hidden text-sm lg:inline">Notifications</span>
          </button>
        </div>
      </aside>

      {/* ------ Main column ------ */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Top bar — mobile only. Rev 5b: respects device safe-area
            insets so the logo/bell/menu never sit under the notch or
            Dynamic Island. md:hidden means desktop + tablet ≥768px are
            unaffected. */}
        <header
          className="sticky top-0 z-20 flex items-center justify-between border-b border-border/50 bg-background/85 py-3 backdrop-blur md:hidden"
          data-testid="app-topbar"
          style={{
            paddingTop: "max(0.75rem, env(safe-area-inset-top))",
            paddingLeft: "max(1rem, env(safe-area-inset-left))",
            paddingRight: "max(1rem, env(safe-area-inset-right))",
          }}
        >
          <BrandLogo size="sm" />
          <div className="flex items-center gap-2">
            {showUpgradeLink ? (
              <NavLink
                to="/upgrade"
                data-testid="nav-upgrade-link-mobile"
                data-nudge-active={nudgeActive ? "true" : undefined}
                className={[
                  "inline-flex h-8 items-center gap-1 rounded-full bg-[#FFC630] px-3 text-[11px] font-bold text-black shadow-sm",
                  nudgeActive ? "pulse-nudge" : "",
                ].join(" ")}
                aria-label="Upgrade"
              >
                <Sparkles className="h-3 w-3" aria-hidden />
                Upgrade
              </NavLink>
            ) : null}
            {isAdmin ? (
              <NavLink
                to="/admin"
                data-testid="nav-admin-link-mobile"
                className="relative flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground hover:bg-secondary hover:text-foreground"
                aria-label="Admin"
              >
                <Shield className="h-[18px] w-[18px]" aria-hidden />
              </NavLink>
            ) : null}
            <button
              type="button"
              aria-label="Notifications"
              onClick={() => navigate("/more")}
              className="relative flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
              data-testid="topbar-notifications"
            >
              <Bell className="h-[18px] w-[18px]" aria-hidden />
              {badge > 0 ? (
                <span
                  className="absolute right-1 top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold text-primary-foreground"
                  data-testid="notification-badge"
                >
                  {badge > 9 ? "9+" : badge}
                </span>
              ) : null}
            </button>
          </div>
        </header>

        <BetaCountdownBanner />

        <main
          className="flex-1 pb-[92px] md:pb-10"
          data-testid="app-main"
          style={{
            // iOS safe-area at the very bottom is handled by the tab bar itself.
          }}
        >
          <Outlet />
        </main>

        {/* Bottom tab bar — mobile only */}
        <nav
          className="fixed inset-x-0 bottom-0 z-30 border-t border-border/70 bg-background/95 backdrop-blur md:hidden"
          style={{ paddingBottom: "env(safe-area-inset-bottom, 0)" }}
          aria-label="Primary"
          data-testid="app-tabbar"
        >
          <ul className="mx-auto flex max-w-md items-stretch justify-between px-2 py-1">
            {TABS.map((tab) => (
              <li key={tab.key} className="flex-1">
                <TabBarLink tab={tab} active={activeTab === tab.key} />
              </li>
            ))}
          </ul>
        </nav>
      </div>
      {/* First-time PWA install prompt — one-shot per user, self-guards. */}
      <InstallPromptModal />
    </div>
  );
}

function SidebarLink({ tab, active }) {
  const Icon = tab.icon;
  return (
    <NavLink
      to={tab.to}
      end={tab.to === "/"}
      data-testid={`tab-${tab.key}`}
      aria-current={active ? "page" : undefined}
      className={({ isActive }) =>
        [
          "flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors",
          isActive || active
            ? "bg-primary/12 text-primary"
            : "text-muted-foreground hover:bg-secondary hover:text-foreground",
        ].join(" ")
      }
    >
      <Icon className="h-5 w-5" aria-hidden />
      <span className="hidden lg:inline">{tab.label}</span>
    </NavLink>
  );
}

function TabBarLink({ tab, active }) {
  const Icon = tab.icon;
  return (
    <NavLink
      to={tab.to}
      end={tab.to === "/"}
      data-testid={`tab-${tab.key}`}
      aria-current={active ? "page" : undefined}
      className={({ isActive }) =>
        [
          "flex min-h-[44px] w-full flex-col items-center justify-center gap-0.5 rounded-lg px-1 py-1.5 text-[11px] font-medium transition-colors",
          isActive || active
            ? "text-primary"
            : "text-muted-foreground hover:text-foreground",
        ].join(" ")
      }
    >
      <span
        className={[
          "relative flex h-6 w-9 items-center justify-center rounded-full",
          active ? "bg-primary/15" : "",
        ].join(" ")}
      >
        <Icon className="h-[18px] w-[18px]" aria-hidden />
      </span>
      <span>{tab.label}</span>
    </NavLink>
  );
}

export default AppShell;
